/**
 * @graphql cc
 */
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';

import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData';
import {
	handleDeepLinkNavigation,
	NavigationContext,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils';
import {
	scrollToElementWithViewingRoom,
	watchForCommentRepliesAndScroll,
	checkElementExpand,
} from '@confluence/comments-util/entry-points/domUtils';
import { checkElementExpandInEditor } from '@confluence/comments-util/entry-points/editorAnnotations';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { getLogger } from '@confluence/logger';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { usePageContentId } from '@confluence/page-context';
import { ResolvedInlineCommentsDialogLoader } from '@confluence/resolved-inline-comments-dialog/entry-points/ResolvedInlineCommentsDialog';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel';
import {
	ViewValues,
	useCommentsPanelActions,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { fg } from '@confluence/feature-gating';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext';
import { expVal } from '@confluence/feature-experiments';
import { buildDefaultCommentsContentStatusFilters } from '@confluence/comments-data-manager/entry-points/useAllCommentsData';
import { useCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';

import type {
	InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery as InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$data,
	InlineCommentQueryParamsListenerInlineCommentQueryParamsQueryVariables as InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$variables,
} from './__types__/InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery';
import type { InlineCommentQueryParamsQuery_comments_nodes_location_InlineComment as InlineCommentLocation } from './graphql/__types__/InlineCommentQueryParamsQuery';
import { useRetryOnMutation } from './useRetryOnMutation';

const InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery = gql`
	query InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery($commentId: ID!) {
		comments(commentId: $commentId) {
			nodes {
				id
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							resolved
						}
					}
				}
				ancestors {
					id
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineResolveProperties {
								resolved
							}
						}
					}
				}
				parentId
			}
		}
	}
`;
const logger = getLogger('inline-comments:query-param-listener');

export const i18n = defineMessages({
	commentLinkNotFoundFlagTitle: {
		id: 'comments-query-param-handlers.comment-link-not-found.flag.title',
		defaultMessage: 'This comment is not available',
		description:
			'The title for a warning message informing the user they clicked on a link to a comment that no longer exists because it is probably deleted.',
	},
	commentLinkNotFoundFlagDescription: {
		id: 'comments-query-param-handlers.comment-link-not-found.flag.description',
		defaultMessage: 'It may be deleted.',
		description:
			'A warning message informing the user they clicked on a link to a comment that no longer exists because it is probably deleted.',
	},
});

type InlineCommentQueryParamsListenerProps = {
	isArchived: boolean;
	rendererRef: React.RefObject<Element>;
};

type QueryParamData =
	| {
			location?: InlineCommentLocation;
			parentId?: string | null;
			parentLocation?: InlineCommentLocation;
	  }
	| undefined;

export const InlineCommentQueryParamsListenerComponent: React.FC<
	InlineCommentQueryParamsListenerProps & WithFlagsProps
> = ({ isArchived, rendererRef, flags }) => {
	const [isResolveDialogShown, setIsResolveDialogShown] = useState(false);
	const hasHandledCommentLink = useRef(false);
	const { showCommentsPanel } = useShowCommentsPanel();
	const { showModal } = useDialogs();
	const [contentId] = usePageContentId();
	const {
		focusedCommentId,
		replyToCommentId,
		editCommentId,
		removeCommentQueryParams,
		isCommentNotFound,
	} = useInlineCommentQueryParams({
		queryArchivedContent: isArchived,
	});

	const { rendererActions } = useRendererActions();

	const { setCurrentlySelectedThreadKey, setCommentContainerToFocus } = useCommentsPanelActions();
	const commentId = focusedCommentId || replyToCommentId || editCommentId;
	const { contentStatus } = useContentStatus();

	// NEW FLOW: Use useCommentById hooks when experiment is enabled
	const skipNewFlow =
		!commentId || !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false);

	const contentStatusFilters = buildDefaultCommentsContentStatusFilters(contentStatus);

	const {
		comment: foundComment,
		loading: commentLoading,
		error: commentError,
	} = useCommentById({
		commentId: commentId || '',
		contentId: contentId || '',
		contentStatus: contentStatusFilters,
		options: {
			skip: skipNewFlow,
		},
	});

	const {
		comment: parentComment,
		loading: parentLoading,
		error: parentError,
	} = useCommentById({
		commentId: foundComment?.parentId || '',
		contentId: contentId || '',
		contentStatus: contentStatusFilters,
		options: {
			skip: skipNewFlow || !foundComment?.parentId,
		},
	});

	// OLD FLOW: Direct GraphQL query (fallback when experiment is disabled)
	const { data, loading, error } = useQuery<
		InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$data,
		InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$variables
	>(InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery, {
		variables: {
			commentId: commentId!,
		},
		skip: !commentId || expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
	});
	if (error) {
		logger.error`An error occurred when querying the focused comment status; ${error}`;

		// TODO: If this query fails, there isn't much we can do, so just track it with experience tracker?

		markErrorAsHandled(error);
	}

	const queryParamData: QueryParamData = useMemo(() => {
		if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
			// NEW FLOW: Extract data from useCommentById hooks
			if (!commentError && !commentLoading && foundComment) {
				const location = foundComment.location as InlineCommentLocation | undefined;
				const parentId = foundComment.parentId;

				const parentLocation =
					parentId && !parentError && !parentLoading && parentComment
						? (parentComment.location as InlineCommentLocation)
						: undefined;

				return {
					location,
					parentId,
					parentLocation,
				};
			}

			return undefined;
		} else {
			// OLD FLOW: Extract data from GraphQL query
			if (!error && !loading && data) {
				const location = data.comments?.nodes?.[0]?.location as InlineCommentLocation | undefined;
				const parentId = data.comments?.nodes?.[0]?.parentId;
				const parentLocation = data.comments?.nodes?.[0]?.ancestors?.[0]?.location as
					| InlineCommentLocation
					| undefined;

				return {
					location,
					parentId,
					parentLocation,
				};
			}

			return undefined;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		contentId,
		commentId,
		data,
		loading,
		error,
		foundComment,
		commentLoading,
		commentError,
		parentComment,
		parentLoading,
		parentError,
	]);

	const queryParamDataExt = useMemo(() => {
		const { location, parentLocation } = queryParamData || {};
		return location?.inlineMarkerRef
			? {
					annotationId: location.inlineMarkerRef,
					isResolved: Boolean(location.inlineResolveProperties?.resolved),
				}
			: {
					annotationId: parentLocation?.inlineMarkerRef,
					isResolved: Boolean(parentLocation?.inlineResolveProperties?.resolved),
				};
	}, [queryParamData]);

	const tryToFocusAndScrollActiveCommentCallback = useCallback(() => {
		return tryToFocusAndScrollActiveComment({
			parentId: queryParamData?.parentId,
			annotationId: queryParamDataExt?.annotationId || null,
			commentId: commentId || null,
		});
	}, [queryParamData, queryParamDataExt, commentId]);

	useRetryOnMutation({
		callback: tryToFocusAndScrollActiveCommentCallback,
		elementRef: rendererRef,
		skip: !commentId || !queryParamData || queryParamDataExt.isResolved,
	});

	useEffect(() => {
		if (!queryParamData || hasHandledCommentLink.current) {
			return;
		}

		const { location, parentId } = queryParamData;
		const { annotationId } = queryParamDataExt;

		if (location) {
			if (queryParamDataExt.isResolved) {
				if (fg('confluence-frontend-comments-panel')) {
					showCommentsPanel({ openWithView: ViewValues.RESOLVED });
					if (annotationId) {
						setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE);
						setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
					}
				} else {
					if (!isResolveDialogShown) {
						setIsResolveDialogShown(true);
						// @ts-expect-error - Incompatible types but works fine
						showModal(ResolvedInlineCommentsDialogLoader, {
							context: 'renderer',
							contentId: contentId!,
							focusedCommentData: {
								id: commentId,
								parentCommentId: parentId,
							},
							onClose: () => {
								removeCommentQueryParams();
								setIsResolveDialogShown(false);
							},
						});
					}
				}
			} else {
				if (fg('confluence-frontend-comments-panel')) {
					// Check if annotation exists in renderer only when we need to make the decision
					const annotationExists = rendererActions
						? rendererActions
								.getAnnotationMarks()
								?.some((mark: any) => mark.attrs.id === annotationId)
						: false;

					if (
						annotationId &&
						!annotationExists &&
						fg('confluence_frontend_new_dangling_comments_ux')
					) {
						setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE); // needed so that the view knows which thread to scroll to after rendering the threads
						setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
						showCommentsPanel({ openWithView: ViewValues.DELETED_CONTENT });
					}
				}
			}
			hasHandledCommentLink.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryParamData, isResolveDialogShown, rendererActions]);

	// Only display the "comment not found" flag once
	useEffect(() => {
		if (isCommentNotFound) {
			const VIEWPAGE_COMMMENT_LINK_NOT_FOUND_FLAG_ID = `viewpage-comment-link-not-found-${commentId}`;
			void flags.showWarningFlag({
				id: VIEWPAGE_COMMMENT_LINK_NOT_FOUND_FLAG_ID,
				title: <FormattedMessage {...i18n.commentLinkNotFoundFlagTitle} />,
				description: <FormattedMessage {...i18n.commentLinkNotFoundFlagDescription} />,
			});
		}
	}, [commentId, flags, isCommentNotFound]);

	return null;
};

const tryToFocusAndScrollActiveComment = ({
	parentId,
	annotationId,
	commentId,
}: {
	parentId: string | null | undefined;
	annotationId: string | null;
	commentId: string | null;
}): boolean => {
	const mark = document.querySelector<HTMLElement>(`[data-id="${annotationId}"]`);

	if (mark) {
		let focusedMarkerRef = document.querySelector("[data-has-focus='true']");
		// If the already open container is showing the correct comment, don't send another click event to open it
		if (focusedMarkerRef?.id !== annotationId) {
			const emitter = getRendererAnnotationEventEmitter();
			if (fg('confluence_comment_notification_deeplink')) {
				checkElementExpand(mark, true);
				emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
					annotationIds: [annotationId],
					eventTarget: mark,
				});
			}
			if (!fg('confluence_comment_notification_deeplink')) {
				setTimeout(() => {
					checkElementExpandInEditor(mark);
					// Excerpt(nested renderer) like macros re-render this component making the
					// highlight to be removed from the DOM
					// so, need to check the focus attribute once the ON_ANNOTATION_CLICK gets triggered
					focusedMarkerRef = document.querySelector("[data-has-focus='true']");
					if (
						!focusedMarkerRef &&
						emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
							annotationIds: [annotationId],
							eventTarget: mark,
						})
					) {
						emitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, {
							annotationId,
						});
					}
					// We need the timeout to give the document the chance to make the marks properly
				}, 1000);
			}
		}

		// Using common utility from comments-util

		// if the comment is a reply, don't scroll to it here, we'll handle the reply scrolling after the reply has rendered to avoid double scrolling
		if (!parentId && !fg('confluence-fe-comments-deep-link-behavior')) {
			abortAll('new_interaction', `focused-comment-scroll`);
			// note that this is for Renderer for now
			scrollToElementWithViewingRoom({
				element: mark,
				useInstantScroll: true,
			});
		}

		// Only proceed if we have a valid commentId
		if (commentId && fg('confluence-fe-comments-deep-link-behavior')) {
			// Use new unified navigation when feature flag is enabled
			if (fg('confluence_comments_unified_navigation')) {
				const success = handleDeepLinkNavigation({
					scrollBehavior: ScrollBehavior.INSTANT,
					context: NavigationContext.RENDERER,
					block: 'center',
					markElement: mark,
					commentId,
				});

				if (!success) {
					// Fallback to original behavior if unified navigation fails
					abortAll('new_interaction', `focused-comment-scroll`);
					scrollToElementWithViewingRoom({
						element: mark,
						useInstantScroll: true,
					});
				}
			} else {
				// Check if comment already exists in DOM
				const existingComment = document.querySelector(`[data-comment-id="${commentId}"]`);
				if (existingComment) {
					const showMoreButton = document.querySelector(
						'[data-testid="comments-panel-thread-show-hidden-replies"]',
					);
					const allRepliesVisible = !showMoreButton;

					if (allRepliesVisible) {
						existingComment.scrollIntoView({
							behavior: 'instant',
							block: 'center',
						});
					} else {
						abortAll('new_interaction', `focused-comment-scroll`);
						scrollToElementWithViewingRoom({
							element: mark,
							useInstantScroll: true,
						});
					}
				} else {
					// Watch for comment to be rendered using common utility
					watchForCommentRepliesAndScroll({
						targetCommentId: commentId,
						onScrollReady: (commentElement) => {
							commentElement.scrollIntoView({
								behavior: 'instant',
								block: 'center',
							});
						},
						onFallback: () => {
							abortAll('new_interaction', `focused-comment-scroll`);
							scrollToElementWithViewingRoom({
								element: mark,
								useInstantScroll: true,
							});
						},
						markElement: document.querySelector<HTMLElement>(`[data-id="${annotationId}"]`),
					});
				}
			}
		}
	}

	return !!mark;
};

export const InlineCommentQueryParamsListener = withFlags(
	InlineCommentQueryParamsListenerComponent,
);
